/**
 * @module builder
 */
import type { AreaSubstrate, OptionsDocument, RatesDocument } from 'paintscout'
import type { EditableRate } from '../../../options'
import { getRates } from '../../../options/get-rates/get-rates'

/**
 * A lookup from an area substrate to its rate as configured in the rates document.
 *
 * The rate snapshot stored on a substrate has no default_products, so a swap that wants the rate's
 * own coverage for the new product (resolveSwappedCoverage step 3) has to read the configured rate.
 * Shared by the Swap Products dialog and the tier swap, so both find the same rate for a surface.
 *
 * Inactive rates are included: an estimate can still hold a surface built from one. Without a rates
 * document every lookup returns null and the swap skips the rate step.
 */
export function createSubstrateRateLookup(args: {
  options: OptionsDocument
  rates?: RatesDocument | null
}): (substrate: AreaSubstrate) => EditableRate | null {
  const { options, rates } = args

  // Keyed by a JSON pair so no section/name spelling can collide with another.
  const rateKey = (section: string | undefined, name: string) => JSON.stringify([section ?? null, name])
  const ratesByKey = new Map<string, EditableRate>()
  if (rates?.rates) {
    for (const rate of getRates({ options, rates, inactive: true, canDeactivate: false })) {
      ratesByKey.set(rateKey(rate.rateSection, rate.name), rate)
    }
  }

  return (substrate) =>
    substrate?.rate?.name ? ratesByKey.get(rateKey(substrate.rate.section, substrate.rate.name)) ?? null : null
}
